import * as aq from 'arquero';

import { filterByDateRange } from '../../../providers/TransactionsProvider';
import { TransactionGroupType } from '../utils';

import type { CalendarDate } from '@internationalized/date';
import type { ColumnTable } from 'arquero';

export interface IProcessedChartData {
  period: string; // YYYY-MM-DD format
  totalAmount: number;
}

interface IProcessDataOptions {
  transactions: ColumnTable;
  startDate: CalendarDate;
  endDate: CalendarDate;
  groupType: TransactionGroupType;
  negateAmounts?: boolean;
}

interface IRollupDataItem {
  year: number;
  month?: number; // 1-indexed (CalendarDate format), optional for year grouping
  day?: number;
  totalAmount: number;
}

function fillInMissingData(
  rollupData: IRollupDataItem[],
  startDate: CalendarDate,
  endDate: CalendarDate,
  groupType: TransactionGroupType
): IProcessedChartData[] {
  const filledData: IProcessedChartData[] = [];

  if (groupType === TransactionGroupType.DAY) {
    // Fill in missing days up to endDate using CalendarDate
    let currentDate = startDate;

    while (currentDate.compare(endDate) <= 0) {
      const year = currentDate.year;
      const month = currentDate.month; // Keep 1-indexed
      const day = currentDate.day;

      const existing = rollupData.find((item) => item.year === year && item.month === month && item.day === day);

      // Convert CalendarDate to YYYY-MM-DD string using toString()
      filledData.push({
        period: currentDate.toString(),
        totalAmount: existing?.totalAmount ?? 0,
      });

      currentDate = currentDate.add({ days: 1 });
    }
  } else if (groupType === TransactionGroupType.MONTH) {
    // Fill in missing months up to endDate using CalendarDate
    // Start at the first day of the start month
    let currentDate = startDate.set({ day: 1 });

    // Create a date for the first day of the end month for comparison
    const endMonthStart = endDate.set({ day: 1 });

    while (currentDate.compare(endMonthStart) <= 0) {
      const year = currentDate.year;
      const month = currentDate.month; // Keep 1-indexed

      const existing = rollupData.find((item) => item.year === year && item.month === month);

      // Convert CalendarDate to YYYY-MM-DD string using toString()
      filledData.push({
        period: currentDate.toString(),
        totalAmount: existing?.totalAmount ?? 0,
      });

      currentDate = currentDate.add({ months: 1 });
    }
  } else if (groupType === TransactionGroupType.YEAR) {
    // Fill in missing years up to endDate
    // Start at Jan 1 of the start year
    let currentDate = startDate.set({ month: 1, day: 1 });

    // Create a date for Jan 1 of the end year for comparison
    const endYearStart = endDate.set({ month: 1, day: 1 });

    while (currentDate.compare(endYearStart) <= 0) {
      const year = currentDate.year;

      const existing = rollupData.find((item) => item.year === year);

      // Convert CalendarDate to YYYY-MM-DD string using toString()
      filledData.push({
        period: currentDate.toString(),
        totalAmount: existing?.totalAmount ?? 0,
      });

      currentDate = currentDate.add({ years: 1 });
    }
  }

  return filledData;
}

export function groupTransactions({
  transactions,
  startDate,
  endDate,
  groupType,
  negateAmounts = false,
}: IProcessDataOptions): IProcessedChartData[] {
  // Filter by date range using CalendarDate comparison
  let filteredTable = filterByDateRange(transactions, startDate, endDate);

  // Negate amounts if needed
  if (negateAmounts) {
    filteredTable = filteredTable.derive({
      amount: (d: { amount: number }) => -d.amount,
    });
  }

  // Group by day, month, or year based on groupType
  let rollup;
  if (groupType === TransactionGroupType.DAY) {
    rollup = filteredTable
      .groupby({
        year: (d: { date: CalendarDate }) => d.date.year,
        month: (d: { date: CalendarDate }) => d.date.month, // Keep 1-indexed
        day: (d: { date: CalendarDate }) => d.date.day,
      })
      .rollup({
        totalAmount: aq.op.sum('amount'),
      })
      .orderby('year', 'month', 'day');
  } else if (groupType === TransactionGroupType.MONTH) {
    rollup = filteredTable
      .groupby({
        year: (d: { date: CalendarDate }) => d.date.year,
        month: (d: { date: CalendarDate }) => d.date.month, // Keep 1-indexed
      })
      .rollup({
        totalAmount: aq.op.sum('amount'),
      })
      .orderby('year', 'month');
  } else {
    // YEAR grouping
    rollup = filteredTable
      .groupby({
        year: (d: { date: CalendarDate }) => d.date.year,
      })
      .rollup({
        totalAmount: aq.op.sum('amount'),
      })
      .orderby('year');
  }

  const rollupData = rollup.objects() as IRollupDataItem[];

  // Fill in missing periods with zeros - returns YYYY-MM-DD strings
  const filledData = fillInMissingData(rollupData, startDate, endDate, groupType);

  // Format totalAmount to 2 decimal places
  return filledData.map((item) => ({
    period: item.period,
    totalAmount: Number(item.totalAmount.toFixed(2)),
  }));
}
